import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  useEffect, useCallback, useState, useMemo,
} from 'react';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import {
  saveBrochureRequest, sendBrochureRequest, updateBrochureField, updateBrochureFields,
} from '../../../../../store/slices/brochure';
import { SearchStatus, TravelTypes } from '../../../../../constants';
import EmailToClientForm from '../../../../EmailToClientForm';

import Checkbox from '../../../../Checkbox';
import ConfirmModal from '../../../../ConfirmModal';

const BrochureEmailForm = ({ tripId, id }) => {
  const { email, name, travelAgency } = useSelector((store) => store.user);
  const { brochure } = useSelector((state) => state.brochure);
  const {
    saveBrochureStatus,
    saveBrochureError,
    sendBrochureStatus,
    sendBrochureError,
  } = brochure;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const router = useRouter();
  const { brochureId: brochureIdFromUrl } = router?.query;
  const brochureSettings = (travelAgency && travelAgency.settings
    && travelAgency.settings.brochureSettings) || {};
  const displayLabel = (brochureSettings.DisplayName || 'brochure').toLowerCase();
  const doHideSentCopyToMe = !!brochureSettings.doHideCheckboxes;
  const doHideCreatedFor = !!brochureSettings.doHideCreatedFor;
  const doShowEmptyEmailMessage = !!brochureSettings.doShowEmptyEmailMessage;
  const doHideShowPricing = !!brochureSettings.doHideShowPricing;
  const doHideUpdatePricingCheckbox = !!brochureSettings.doHideUpdatePricingCheckbox;
  const defaultUpdatePricing = !!brochureSettings.defaultUpdatePricing;
  const brochures = useSelector((state) => state.brochure.brochures[id] || {});
  const doAllowBookDefaultValue = !!brochureSettings.doAllowBookDefaultValue;
  const doShowBrochureEditor = !!brochureSettings.doShowBrochureEditor;

  const [showConfirm, setShowConfirm] = useState(false);

  const onSendClick = () => {
    dispatch(sendBrochureRequest({
      ...brochure,
      brochureId: tripId,
      savedBrochureId: brochure.id,
    }));
    track('BrochureEmailForm.onSendClick');
  };

  const allTrips = Object.values(brochure.trips);
  const hasIncompleteFlightSegments = useMemo(() => allTrips
    .some((trip) => trip.tripSegments.some(
      (segment) => segment.itinerary.isIncompleteItinerary,
    )), [allTrips]);

  // Remove invalid flights from brochure
  const validTrips = useMemo(() => allTrips
    .map((trip) => ({
      ...trip,
      tripSegments: trip.tripSegments.filter(
        (segment) => !segment.itinerary.isIncompleteItinerary,
      ),
    }))
    .filter((trip) => trip.tripSegments.length > 0), [allTrips]);

  const hasAnyTripSegments = validTrips && validTrips.length;

  const handleSaveBrochure = () => {
    if (!hasAnyTripSegments) return;
    dispatch(updateBrochureField({
      fieldName: 'saveBrochureStatus',
      fieldValue: SearchStatus.SEARCHING,
    }));
    dispatch(saveBrochureRequest({
      ...{ ...brochure, trips: validTrips },
      brochureId: brochureIdFromUrl ?? tripId,
      savedBrochureId: brochureIdFromUrl ?? brochure.id,
      resultsPageUrl: window.location.href,
      doSaveToDatabase: true,
    }));
    track('BrochureEmailForm.onSavePreviewClick');
  };

  const onSavePreviewClick = () => {
    if (hasIncompleteFlightSegments) {
      setShowConfirm(true);
      return;
    }
    handleSaveBrochure();
  };

  const handleInputChange = (event) => {
    let fieldName = event.target.name;
    let fieldValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    // if (fieldName === 'emailMessage' && brochure.brochureQAs) {
    //   fieldName = 'brochureQAs';
    // }
    dispatch(updateBrochureField({
      brochureId: tripId,
      fieldName,
      fieldValue,
    }));
    // TODO: are we storing both emailRecipientsString
    // and emailRecipients in redux? why? refactor
    if (fieldName === 'emailRecipientsString') {
      fieldName = 'emailRecipients';
      fieldValue = _.map((fieldValue || '').split(','), (e) => e.trim());
      dispatch(updateBrochureField({
        brochureId: tripId,
        fieldName,
        fieldValue,
      }));
    }
  };

  const getDefaultEmailMessage = useCallback((agentName, uniqueModesStr) => {
    if (doShowEmptyEmailMessage) return '';
    return travelAgency.defaultBrochureText
      ? travelAgency.defaultBrochureText.replace('{{agentName}}', agentName).replace('{{itineraries}}', uniqueModesStr)
      : `Hello,

Here is a ${displayLabel} with some ${uniqueModesStr} options for you to review.

Please let me know if you would like to proceed or have any questions.

Regards,
${agentName}`;
  }, [displayLabel, doShowEmptyEmailMessage, travelAgency.defaultBrochureText]);

  useEffect(() => {
    if (!brochure.isSavedToDatabase && !brochureIdFromUrl) {
      const uniqModes = _.uniq(_.map(_.flatMap(Object.values(brochure.trips), (t) => t.tripSegments), 'itinerary.mode'));
      let uniqueModesStr = '';
      _.each(uniqModes, (m, index) => {
        uniqueModesStr += (m === TravelTypes.carrental ? 'rental car' : Object.keys(TravelTypes)[m]);
        if (index < uniqModes.length - 2) {
          uniqueModesStr += ', ';
        } else if (index < uniqModes.length - 1) {
          uniqueModesStr += ' and ';
        }
      });
      const agentName = name || email.split('@')[0];
      dispatch(updateBrochureField({ brochureId: tripId, fieldName: 'brochureQAs', fieldValue: '' }));
      dispatch(updateBrochureField({
        brochureId: tripId,
        fieldName: 'emailMessage',
        fieldValue: getDefaultEmailMessage(agentName, uniqueModesStr),
      }));
    }
  }, [brochure.isSavedToDatabase, brochure.trips, dispatch, email, name, brochureIdFromUrl,
    travelAgency.defaultBrochureText, tripId, displayLabel, getDefaultEmailMessage]);

  useEffect(() => {
    let emailRecipientsString = '';

    if (brochure && brochure.emailRecipients && brochure.emailRecipients.length > 0) {
      emailRecipientsString = brochure.emailRecipients.join(',');
    } else if (brochures && brochures.emailRecipients && brochures.emailRecipients.length > 0) {
      emailRecipientsString = brochures.emailRecipients.join(',');
    }

    const resolvedDoShowPricing = brochure?.doShowPricing !== undefined
      && brochure?.doShowPricing !== null
      ? brochure.doShowPricing : travelAgency?.defaultDoShowPricingToAnonymous ?? true;

    dispatch(updateBrochureFields({
      brochureId: brochureIdFromUrl ?? tripId,
      fields: {
        ...brochure,
        fromEmail: email,
        saveBrochureError: null,
        sendBrochureError: null,
        saveBrochureStatus: SearchStatus.INITIAL,
        sendBrochureStatus: SearchStatus.INITIAL,
        doUpdatePriceOnPageLoad: brochure?.doUpdatePriceOnPageLoad ?? defaultUpdatePricing,
        doAllowBook: brochure?.doAllowBook ?? doAllowBookDefaultValue,
        doShowPricing: resolvedDoShowPricing,
        emailRecipientsString,
        doShowBrochureEditor: brochures?.editorHTML || false,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tripId, email]);

  useEffect(() => {
    if (_.isEmpty(brochure?.trips)) return;
    dispatch(saveBrochureRequest({
      ...brochure,
      brochureId: brochureIdFromUrl ?? tripId,
      savedBrochureId: brochureIdFromUrl ?? brochure.id,
      doSaveToDatabase: false,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <EmailToClientForm
        brochure={brochure}
        createdFor={doHideCreatedFor ? undefined : brochure.createdFor || ''}
        fromEmail={brochure.fromEmail || ''}
        emailRecipientsString={brochure.emailRecipientsString || ''}
        emailSubject={brochure.emailSubject || ''}
        doSendCopyToAgent={brochure.doSendCopyToAgent || false}
        doAllowBook={brochure.doAllowBook || false}
        doShowPricing={brochure.doShowPricing}
        customCheckboxes={doHideUpdatePricingCheckbox ? <></> : (
          <Checkbox
            checked={brochure.doUpdatePriceOnPageLoad}
            name="doUpdatePriceOnPageLoad"
            onChange={handleInputChange}
            value={brochure.doUpdatePriceOnPageLoad}
            label={`Update prices whenever customer opens ${displayLabel}`}
          />
        )}
        onInputChange={handleInputChange}
        doEnableSave={
          saveBrochureStatus !== SearchStatus.SEARCHING
          && sendBrochureStatus !== SearchStatus.SEARCHING
        }
        onSavePreviewClick={onSavePreviewClick}
        saveStatus={saveBrochureStatus}
        saveError={saveBrochureError}
        saveLabel={id ? `Update ${displayLabel}` : `Save ${displayLabel}`}
        doEnableSend={
          saveBrochureStatus !== SearchStatus.SEARCHING
          && brochure
          && brochure.isSavedToDatabase
          && sendBrochureStatus !== SearchStatus.SEARCHING
        }
        doEnableCopyLink={
          saveBrochureStatus !== SearchStatus.SEARCHING
          && brochure
          && brochure.isSavedToDatabase
          && sendBrochureStatus !== SearchStatus.SEARCHING
        }
        onSendClick={onSendClick}
        sendStatus={sendBrochureStatus}
        sendError={sendBrochureError}
        sendLabel={`Send ${displayLabel}`}
        emailMessage={brochure.emailMessage}
        doHideSentCopyToMe={doHideSentCopyToMe}
        doHideShowPricing={doHideShowPricing}
        doShowBrochureEditor={doShowBrochureEditor}
      />
      {showConfirm && (
        <ConfirmModal
          className="modal-safe-click-zone"
          open={showConfirm}
          onConfirm={() => {
            setShowConfirm(false);
            handleSaveBrochure();
          }}
          onCancel={() => setShowConfirm(false)}
          okButtonLabel={id ? 'Update' : 'Save'}
          disableOkButton={!hasAnyTripSegments}
          message={!hasAnyTripSegments ? 'There are no valid trip segments to save. Please add the missing segments.' : (
            <div>
              <div className="mb-2">
                <span role="img" aria-label="warning">⚠️</span>
                {' '}
                <b>Some flight options are incomplete.</b>
              </div>
              The return (inbound) segments are missing.
              These incomplete options will be removed before saving.
              Do you want to continue?
            </div>
          )}
        />
      )}
    </div>
  );
};

BrochureEmailForm.propTypes = {
  tripId: PropTypes.string,
  id: PropTypes.string,
};

BrochureEmailForm.defaultProps = {
  tripId: null,
  id: null,
};

export default BrochureEmailForm;
